import { DocumentSchema } from '../../../documents/types';
import { getDocumentSchema } from '../../../../services/documentSchemaService';
import { CaseReport } from '../../../../services/reportService';
import {
    CaseDocumentSchemaKey,
    getDocumentBySchemaKey,
} from './caseWorkspaceUtils';

export interface SchemaPayload {
    key: string;
    version: number;
    schema: DocumentSchema;
}

const toSchemaPayload = (record: {
    key: string;
    version: number;
    schema: Record<string, any>;
}): SchemaPayload => ({
    key: record.key,
    version: record.version,
    schema: record.schema as DocumentSchema,
});

/**
 * Resolves the schema a workspace panel should render with.
 *
 * - Existing document: its own stored schema version. The case response
 *   embeds the exact schema record, so this normally costs no request; if the
 *   embed is missing the schema is fetched by the document's version.
 * - No document yet: the latest published version.
 */
export const resolveWorkspaceSchema = async (
    schemaKey: string,
    caseData: CaseReport | null
): Promise<SchemaPayload> => {
    const document = getDocumentBySchemaKey(
        caseData,
        schemaKey as CaseDocumentSchemaKey
    );

    if (document?.document?.schema) {
        return {
            key: schemaKey,
            version: document.schemaVersion,
            schema: document.document.schema as DocumentSchema,
        };
    }

    if (document?.schemaVersion) {
        return toSchemaPayload(
            await getDocumentSchema(schemaKey, document.schemaVersion)
        );
    }

    return toSchemaPayload(await getDocumentSchema(schemaKey));
};

export const resolveWorkspaceSchemas = async (
    schemaKeys: string[],
    caseData: CaseReport | null
): Promise<Record<string, SchemaPayload>> => {
    const payloads = await Promise.all(
        schemaKeys.map((key) => resolveWorkspaceSchema(key, caseData))
    );

    return payloads.reduce((acc, payload) => {
        acc[payload.key] = payload;
        return acc;
    }, {} as Record<string, SchemaPayload>);
};
